import { useEffect, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import DateFilter from '@inovua/reactdatagrid-enterprise/DateFilter';
import DataTable, {
  ActionsRenderer,
  enumRenderer,
  dateRenderer,
  EnumRendererRenderProps,
  RowProps,
  dateSort,
} from 'components/DataTable';
import {
  useFetchUsers,
  useTableData,
  useDeleteUser,
  useBulkDeleteUsers,
  useBulkUpdateUsers,
  useUpdateUser,
  useToast,
  useFetchRoles,
} from 'hooks';
import { useUser } from 'contexts/userContext';
import { AccountStatus, accountStatusMap } from 'constants/accountStatuses';
import { getUserName } from 'utils/users';
import { Button, Modal, ModalBody, ModalHeader } from 'components/UI';
import { ActionLoadingCallback } from 'components/DataTable/Actions/Action';
import { checkIfError } from 'utils/helpers';
import { ActionCallbackItem } from 'components/DataTable/Actions';
import { IOption } from 'types';
import User from 'components/common/User';
import { SortDirection } from 'constants/queries';
import { FaUserTag } from 'react-icons/fa';
import { TypeRowProps } from '@inovua/reactdatagrid-enterprise/types';
import BulkAssignRoleForm, { BulkAssignRoleFormValues } from './BulkAssignRoleForm';
import { IUserResource, IUserResourceWithRelations } from '../../../../server/types/models/IUser';
import { IRoleResource } from '../../../../server/types';

const defaultColumnInfo = {
  defaultFlex: 1,
  minWidth: 160,
};

const columns = [
  {
    header: 'Registered',
    name: 'createdAt',
    dateFormat: 'YYYY/MM/DD',
    operator: 'eq',
    type: 'date',
    sort: dateSort,
    filterEditor: DateFilter,
    render: dateRenderer,
    ...defaultColumnInfo,
    minWidth: 120,
  },
  {
    header: 'Created',
    name: 'created',
  },
  {
    header: 'First Name',
    name: 'firstName',
    ...defaultColumnInfo,
  },
  {
    header: 'Last Name',
    name: 'lastName',
    ...defaultColumnInfo,
  },
  {
    header: 'Email',
    name: 'email',
    ...defaultColumnInfo,
  },
  {
    header: 'Role',
    name: 'roleName',
    ...defaultColumnInfo,
  },
  {
    header: 'Status',
    name: 'status',
    render: (renderProps: EnumRendererRenderProps) => enumRenderer(renderProps, accountStatusMap, 'Approved'),
    ...defaultColumnInfo,
  },
  {
    header: 'Status Format',
    name: 'statusFormat',
  },
  {
    header: 'Company',
    name: 'company',
    ...defaultColumnInfo,
  },
  {
    header: 'Phone',
    name: 'phone',
    ...defaultColumnInfo,
  },
  {
    header: 'Country',
    name: 'country',
    ...defaultColumnInfo,
  },
  {
    header: 'State/Province',
    name: 'state',
    ...defaultColumnInfo,
  },
  {
    header: 'ZIP/Postal Code',
    name: 'zipCode',
    ...defaultColumnInfo,
  },
  {
    header: 'Registration URL',
    name: 'registerUrl',
    ...defaultColumnInfo,
  },
];

const handleCheckPending = (data: IUserResource) =>
  data.status && data.status.toLowerCase() === accountStatusMap.pending.toLowerCase();

const Users = () => {
  const [tableData, setTableData, { deleteItem, editItem, editItems, addItems, bulkDeleteItems }] =
    useTableData<IUserResourceWithRelations>();

  const [fetchUsers, { error: fetchUsersError, loading: fetchUsersLoading }] = useFetchUsers();

  const [fetchRoles, { error: fetchRolesError, loading: fetchRolesLoading }] = useFetchRoles();

  const [user] = useUser();

  const [modalOpen, setModalOpen] = useState<string | Boolean>(false);

  const [roles, setRoles] = useState<IRoleResource[]>([]);

  const [modalHeader, setModalHeader] = useState('Create a User');

  const [modalBody, setModalBody] = useState<React.ReactElement>();

  const [deleteUser, { error: deleteError }] = useDeleteUser();

  const [updateUser, { error: updateError }] = useUpdateUser();

  const [showToast] = useToast();

  const [bulkUpdateUsers, { loading: bulkUpdateLoading, error: bulkUpdateError }] = useBulkUpdateUsers();

  const [bulkDeleteUsers, { loading: bulkDeleteLoading, error: bulkDeleteError }] = useBulkDeleteUsers();

  useEffect(() => {
    if (fetchUsersError || fetchRolesError || deleteError || bulkDeleteError || bulkUpdateError || updateError) {
      const error =
        fetchUsersError ?? fetchRolesError ?? deleteError ?? bulkDeleteError ?? bulkUpdateError ?? updateError;

      if (!error) return;

      showToast({
        message: error,
        status: 'error',
      });
    }
  }, [fetchUsersError, deleteError, bulkDeleteError, bulkUpdateError, updateError]);

  const handleDelete = async (itemData: IUserResource, _: IUserResource, cb: ActionLoadingCallback) => {
    if (!itemData?.id) return;

    cb(true);

    if (user?.id === itemData.id) {
      showToast({
        message: 'You cannot delete yourself.',
        status: 'error',
      });
      return;
    }
    await deleteUser(itemData.id);
    deleteItem(itemData.id, 'id');
    cb(false);
    showToast({
      message: `${getUserName(itemData)} deleted successfully.`,
    });
  };

  const handleUpdate = async (itemData: IUserResource, item: Partial<IUserResource>, cb: ActionLoadingCallback) => {
    if (!itemData?.id) return;

    cb(true);
    const index = tableData.findIndex((tableItem) => tableItem.id === itemData.id);

    const result = await updateUser(itemData.id, { ...item, role: (item.role as IRoleResource)?.id });

    if (checkIfError(result)) return;

    editItem(index, result);
    cb(false);

    showToast({
      message: `${getUserName(result)} ${result.status} successfully.`,
    });
  };

  const handleBulkDelete = async ({ ids }: ActionCallbackItem<IUserResource>, cb: ActionLoadingCallback) => {
    if (!ids || !user?.id) return;

    if (ids.includes(user.id)) {
      showToast({
        status: 'error',
        message: 'You cannot delete yourself.',
      });
      return;
    }

    await bulkDeleteUsers({ ids });
    bulkDeleteItems(ids, 'id');

    showToast({
      message: `${ids.length} user(s) deleted successfully.`,
    });

    cb();
  };

  const handleBulkUpdate = async (
    { ids }: ActionCallbackItem<IUserResource>,
    data: Partial<Omit<IUserResource, 'role'> & { role: string }>,
    cb: ActionLoadingCallback
  ) => {
    if (!ids) return;

    const updatedUsers = await bulkUpdateUsers({ ids, data });

    if (checkIfError(updatedUsers)) {
      cb();
      return;
    }

    const result = Object.keys(data).reduce((acc, key) => {
      const roleData = key === 'role' ? { roleName: (updatedUsers[0].role as IRoleResource).name } : {};

      return {
        ...acc,
        ...roleData,
        [key]: updatedUsers[0][key as keyof IUserResourceWithRelations],
      };
    }, {} as Partial<IUserResourceWithRelations>);

    editItems(ids, result, 'id');

    showToast({
      message: `${ids.length} user(s) ${data?.status ?? 'updated'} successfully.`,
    });

    cb();
  };

  const handleLoadResources = async () => {
    if (!user) return;
    const [usersResult, rolesResult] = await Promise.all([fetchUsers(), fetchRoles()]);

    setRoles(!checkIfError(rolesResult) ? rolesResult : []);

    if (checkIfError(usersResult)) return;

    setTableData(usersResult);
  };

  const handleSubmitUser = (result: IUserResourceWithRelations, data?: Partial<IUserResource>) => {
    if (data?.id) {
      const index = tableData.findIndex((tableItem) => tableItem.id === data.id);

      editItem(index, result);
    } else {
      addItems([result]);
    }

    setModalOpen(false);

    const name = getUserName(data?.id ? data : result);

    showToast({
      message: data?.id ? `${name} updated successfully.` : `${name} created successfully.`,
    });
  };

  const handleViewUser: (event?: MouseEvent, rowProps?: TypeRowProps | RowProps) => void = (
    event?: MouseEvent,
    userRow?: TypeRowProps | RowProps
  ) => {
    const defaultRole = roles.find(({ isDefault }) => isDefault);
    const isExisting = userRow?.data;
    const selectedUser = isExisting ? { ...userRow.data, role: userRow.data.role.id } : { role: defaultRole?.id };

    setModalHeader(isExisting ? `Update ${getUserName(selectedUser)}` : 'Create a User');
    setModalBody(
      <User
        onSubmit={(result: IUserResourceWithRelations) => {
          handleSubmitUser(result, selectedUser);
        }}
        roles={
          roles.map(({ id: value, name: label }) => ({
            value,
            label,
          })) as IOption[]
        }
        data={selectedUser}
      />
    );
    setModalOpen('user');
  };

  const handleBulkAssign = (data: IUserResource, cb: ActionLoadingCallback) => {
    setModalOpen(true);
    setModalHeader('Bulk Assign Role');
    setModalBody(
      <BulkAssignRoleForm
        onSubmit={(values: BulkAssignRoleFormValues) => {
          handleBulkUpdate(data, values, cb);
          setModalOpen(false);
        }}
        options={
          roles.map(({ id: value, name: label }) => ({
            value,
            label,
          })) as IOption[]
        }
      />
    );
  };

  useEffect(() => {
    handleLoadResources();
  }, []);

  const inlineActions = [
    {
      type: 'approve',
      renderTitle: (data: IUserResource) => `Approve ${getUserName(data)}?`,
      renderMessage: () => 'You are about to approve this item, do you want to proceed?',
      callback: (data: IUserResource, _item: IUserResource, cb: ActionLoadingCallback) =>
        handleUpdate(data, { status: AccountStatus.APPROVED }, cb),
      condition: handleCheckPending,
    },
    {
      type: 'deny',
      renderTitle: (data: IUserResource) => `Deny ${getUserName(data)}?`,
      renderMessage: () => 'You are about to deny this item, do you want to proceed?',
      callback: (data: IUserResource, _item: IUserResource, cb: ActionLoadingCallback) =>
        handleUpdate(data, { status: AccountStatus.DENIED }, cb),
      condition: handleCheckPending,
    },
    {
      type: 'delete',
      renderTitle: (data: IUserResource) => `Delete ${getUserName(data)}?`,
      renderMessage: () => 'You are about to permanently delete this item, do you want to proceed?',
      callback: handleDelete,
    },
  ];

  const tableActions = [
    {
      type: 'filter',
      callback: () => {},
    },
    {
      type: 'bulkDelete',
      loading: bulkDeleteLoading,
      callback: handleBulkDelete,
    },
    {
      type: 'bulkApprove',
      loading: bulkUpdateLoading,
      callback: (data: IUserResource, cb: ActionLoadingCallback) =>
        handleBulkUpdate(data, { status: AccountStatus.APPROVED }, cb),
    },
    {
      type: 'bulkDeny',
      loading: bulkUpdateLoading,
      callback: (data: IUserResource, cb: ActionLoadingCallback) =>
        handleBulkUpdate(data, { status: AccountStatus.DENIED }, cb),
    },
    {
      type: 'customSelect',
      loading: bulkUpdateLoading,
      callback: handleBulkAssign,
      icon: FaUserTag,
      title: 'Bulk Assign Role',
    },
  ];

  const pageActions = (
    <Flex w="full" alignItems="center" justifyContent="space-between">
      <Button onClick={() => handleViewUser()}>Create User</Button>
    </Flex>
  );

  return (
    <>
      <DataTable<IUserResource>
        id="user-archive-12"
        rows={tableData.map((data) => ({ ...data, roleName: data.role?.name }))}
        columns={[
          ...columns.filter(({ name }) => !['created', 'statusFormat'].includes(name)),
          {
            id: '$actions',
            header: 'Actions',
            render: (renderProps) => <ActionsRenderer<IUserResource> {...renderProps} actions={inlineActions} />,
          },
        ]}
        exportParams={{
          model: 'User',
          fields: columns.map(({ name }) => name),
          removeFields: ['created', 'status'],
          sort: { firstName: SortDirection.ASC },
        }}
        tableStyles={{ minHeight: 'calc(100% - 126px)' }}
        tableContainerProps={{ minW: '100%' }}
        tableProps={{ idProperty: 'id' }}
        pageActions={pageActions}
        loading={fetchUsersLoading || fetchRolesLoading}
        actions={tableActions}
        onRowDoubleClick={handleViewUser}
      />
      <Modal
        closeOnEsc={false}
        closeOnOverlayClick={false}
        size="2xl"
        isCentered
        isOpen={!!modalOpen}
        onClose={() => setModalOpen(false)}
      >
        <>
          <ModalHeader textAlign="center" fontWeight="normal" my={2} fontSize="4xl">
            {modalHeader}
          </ModalHeader>
          <ModalBody pb={8}>{modalBody}</ModalBody>
        </>
      </Modal>
    </>
  );
};

export default Users;
